import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const NaturalLanguageProcessing = () => {
  return (
    <>
      <SEO 
        title="Natural Language Processing (NLP) - AI-Powered Text & Speech Solutions"
        description="Transform unstructured text and speech into actionable insights with Abacies' enterprise NLP solutions. Advanced sentiment analysis, entity recognition, language translation, and conversational AI capabilities."
        keywords="natural language processing, NLP, text analytics, sentiment analysis, entity recognition, language translation, conversational AI, text mining, speech recognition"
      />

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-pearl-white via-white to-blush-light/10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-primary-red/10 text-primary-red rounded-full text-sm font-semibold">
                AI Hub
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Natural Language <span className="text-gradient">Processing</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up">
              Unlock the power of human language with advanced NLP solutions that understand context, extract meaning, and enable intelligent human-machine interactions at enterprise scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Link to="/contact" className="btn-primary">
                Start Your NLP Project
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-6 mt-16 max-w-5xl mx-auto">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-platinum">
              <div className="text-3xl font-bold text-primary-red mb-2">98%+</div>
              <div className="text-sm text-titanium">Accuracy Rate</div>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-platinum">
              <div className="text-3xl font-bold text-primary-red mb-2">100+</div>
              <div className="text-sm text-titanium">Languages Supported</div>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-platinum">
              <div className="text-3xl font-bold text-primary-red mb-2">1M+</div>
              <div className="text-sm text-titanium">Documents Processed</div>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-platinum">
              <div className="text-3xl font-bold text-primary-red mb-2">Real-time</div>
              <div className="text-sm text-titanium">Processing Speed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="section-title">Core NLP Capabilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive natural language understanding and generation powered by state-of-the-art deep learning models
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
                title: "Sentiment Analysis",
                description: "Real-time emotion detection and opinion mining from text across multiple languages and contexts"
              },
              {
                icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
                title: "Entity Recognition",
                description: "Automated extraction of names, locations, organizations, dates, and custom entities from unstructured text"
              },
              {
                icon: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129",
                title: "Language Translation",
                description: "Neural machine translation supporting 100+ languages with context-aware accuracy"
              },
              {
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                title: "Document Understanding",
                description: "Intelligent classification, summarization, and information extraction from documents and reports"
              },
              {
                icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
                title: "Conversational AI",
                description: "Advanced chatbot and virtual assistant capabilities with natural dialogue flow and context retention"
              },
              {
                icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
                title: "Text Classification",
                description: "Automated categorization and tagging of content with custom taxonomies and multi-label support"
              },
              {
                icon: "M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9",
                title: "Speech Recognition",
                description: "Convert speech to text with speaker diarization, punctuation, and real-time transcription"
              },
              {
                icon: "M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122",
                title: "Intent Detection",
                description: "Understand user intentions and map queries to actions with high accuracy across domains"
              },
              {
                icon: "M13 10V3L4 14h7v7l9-11h-7z",
                title: "Text Generation",
                description: "Create human-like content, summaries, and responses using advanced language models"
              }
            ].map((capability, index) => (
              <div key={index} className="group card hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-colors">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                  {capability.title}
                </h3>
                <p className="text-titanium">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="section-title">Advanced NLP Technology Stack</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Built on cutting-edge frameworks and models, optimized for enterprise performance
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-charcoal flex items-center">
                  <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Core Technologies
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Transformer Models:</strong> BERT, GPT, T5, RoBERTa for contextual understanding</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Neural Networks:</strong> LSTM, GRU, and attention mechanisms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Frameworks:</strong> PyTorch, TensorFlow, Hugging Face Transformers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Processing:</strong> spaCy, NLTK, Stanford CoreNLP</span>
                  </li>
                </ul>
              </div>

              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-charcoal flex items-center">
                  <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Advanced Features
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Multi-lingual:</strong> Support for 100+ languages with transfer learning</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Domain Adaptation:</strong> Fine-tuning for industry-specific terminology</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Real-time Processing:</strong> Low-latency inference with GPU acceleration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-red mr-2">•</span>
                    <span className="text-titanium"><strong className="text-charcoal">Scalability:</strong> Distributed processing for high-volume workloads</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="section-title">Industry Applications</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Transform operations across industries with intelligent language understanding
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                industry: "Customer Service",
                icon: "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z",
                applications: [
                  "Automated ticket routing and prioritization",
                  "Intelligent chatbots with 24/7 support",
                  "Sentiment analysis for quality monitoring",
                  "Multi-language customer communication"
                ]
              },
              {
                industry: "Healthcare",
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                applications: [
                  "Clinical documentation and coding",
                  "Medical literature analysis",
                  "Patient feedback analysis",
                  "Drug interaction detection from text"
                ]
              },
              {
                industry: "Financial Services",
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                applications: [
                  "Fraud detection from transaction descriptions",
                  "Regulatory compliance monitoring",
                  "Investment research automation",
                  "Risk assessment from news and reports"
                ]
              },
              {
                industry: "E-commerce & Retail",
                icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
                applications: [
                  "Product review analysis and insights",
                  "Intelligent product recommendations",
                  "Search query understanding",
                  "Automated content generation"
                ]
              },
              {
                industry: "Legal",
                icon: "M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3",
                applications: [
                  "Contract analysis and extraction",
                  "Legal document classification",
                  "Case law research automation",
                  "Compliance document review"
                ]
              },
              {
                industry: "Media & Content",
                icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
                applications: [
                  "Automated content tagging and categorization",
                  "Plagiarism detection",
                  "Content moderation at scale",
                  "Personalized content recommendations"
                ]
              }
            ].map((useCase, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={useCase.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.industry}</h3>
                    <ul className="space-y-2">
                      {useCase.applications.map((app, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-titanium">{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="section-title">Implementation Process</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Structured approach from consultation to deployment
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  step: "01",
                  title: "Requirements Analysis",
                  description: "Understand your business objectives, data sources, and specific NLP needs through comprehensive discovery sessions"
                },
                {
                  step: "02",
                  title: "Data Assessment",
                  description: "Evaluate data quality, volume, and characteristics. Identify preprocessing requirements and data augmentation strategies"
                },
                {
                  step: "03",
                  title: "Model Selection & Training",
                  description: "Choose optimal architectures and pre-trained models. Fine-tune on your domain-specific data for maximum accuracy"
                },
                {
                  step: "04",
                  title: "Integration & Testing",
                  description: "Deploy models via APIs or embedded solutions. Conduct thorough testing across use cases and edge scenarios"
                },
                {
                  step: "05",
                  title: "Monitoring & Optimization",
                  description: "Continuous performance tracking, model retraining, and optimization based on real-world feedback"
                }
              ].map((phase, index) => (
                <div key={index} className="card hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-red to-deep-garnet rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                      {phase.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2 text-charcoal">{phase.title}</h3>
                      <p className="text-titanium">{phase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="section-title">Why Choose Our NLP Solutions</h2>
                <p className="text-titanium mb-8">
                  Enterprise-grade natural language processing that delivers measurable business value through intelligent automation and deep language understanding.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      title: "State-of-the-Art Accuracy",
                      description: "Leverage latest transformer models and deep learning techniques for industry-leading accuracy"
                    },
                    {
                      title: "Scalable Architecture",
                      description: "Process millions of documents with distributed computing and efficient model serving"
                    },
                    {
                      title: "Custom Domain Adaptation",
                      description: "Fine-tuned models specifically trained on your industry terminology and use cases"
                    },
                    {
                      title: "Multi-lingual Support",
                      description: "Native support for 100+ languages with cross-lingual transfer learning"
                    },
                    {
                      title: "Enterprise Security",
                      description: "On-premise deployment options, data encryption, and compliance with industry regulations"
                    },
                    {
                      title: "Continuous Learning",
                      description: "Models that improve over time through active learning and feedback loops"
                    }
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <svg className="w-6 h-6 text-primary-red mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-charcoal mb-1">{benefit.title}</h4>
                        <p className="text-sm text-titanium">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="card bg-gradient-to-br from-primary-red to-deep-garnet text-white">
                  <h3 className="text-2xl font-bold mb-4">Business Impact</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-3xl font-bold mb-1">70%</div>
                      <div className="text-sm opacity-90">Reduction in Manual Processing Time</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold mb-1">85%</div>
                      <div className="text-sm opacity-90">Improvement in Customer Response Time</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold mb-1">60%</div>
                      <div className="text-sm opacity-90">Cost Savings in Operations</div>
                    </div>
                  </div>
                </div>

                <div className="card bg-alabaster">
                  <h3 className="text-xl font-semibold mb-4 text-charcoal">Popular NLP Models</h3>
                  <div className="flex flex-wrap gap-2">
                    {['BERT', 'GPT-4', 'T5', 'RoBERTa', 'XLNet', 'ALBERT', 'DistilBERT', 'ELECTRA', 'DeBERTa'].map((model, idx) => (
                      <span key={idx} className="px-3 py-1 bg-white rounded-lg text-sm text-charcoal border border-platinum">
                        {model}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-charcoal via-graphite to-charcoal text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Unlock the Power of Language?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Let's discuss how our NLP solutions can transform your text data into actionable insights and intelligent automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-primary-red text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-deep-garnet transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-charcoal transition-all duration-300">
              Explore AI Hub
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-16 pt-12 border-t border-white/20">
            <p className="text-sm opacity-75 mb-6">Trusted by leading organizations worldwide</p>
            <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
              <div className="text-sm">Fortune 500 Companies</div>
              <div className="w-1 h-1 bg-white rounded-full"></div>
              <div className="text-sm">Healthcare Providers</div>
              <div className="w-1 h-1 bg-white rounded-full"></div>
              <div className="text-sm">Financial Institutions</div>
              <div className="w-1 h-1 bg-white rounded-full"></div>
              <div className="text-sm">Government Agencies</div>
            </div>
          </div>
        </div>
      </section>

      {/* Related AI Hub Pages */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <h2 className="text-2xl font-bold mb-8 text-charcoal text-center">Related AI Capabilities</h2>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Link to="/ai-hub/intelligent-chatbots" className="card text-center hover:shadow-xl transition-all duration-300 group">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-red/10">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">Intelligent Chatbots</h3>
              <p className="text-sm text-titanium">Conversational AI solutions</p>
            </Link>

            <Link to="/ai-hub/computer-vision" className="card text-center hover:shadow-xl transition-all duration-300 group">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-red/10">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">Computer Vision</h3>
              <p className="text-sm text-titanium">Image & video analysis</p>
            </Link>

            <Link to="/ai-hub/predictive-analytics" className="card text-center hover:shadow-xl transition-all duration-300 group">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-red/10">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">Predictive Analytics</h3>
              <p className="text-sm text-titanium">Forecast trends & patterns</p>
            </Link>

            <Link to="/ai-hub/intelligent-automation" className="card text-center hover:shadow-xl transition-all duration-300 group">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-red/10">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">Intelligent Automation</h3>
              <p className="text-sm text-titanium">Process optimization</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default NaturalLanguageProcessing;